import { IconButton } from '@affine/component';
import { useAutosizeTextarea } from '@affine/component/ui/textarea';
import { useI18n } from '@affine/i18n';
import { SearchIcon } from '@docnboard/icons/rc';
import { cn } from '@udecode/cn';
import { type NodeEntry } from '@udecode/plate';
import { useEditorPlugin, usePluginOption } from '@udecode/plate/react';
import {
  useEditorChat,
  useLastAssistantMessage,
} from '@udecode/plate-ai/react';
import { BlockSelectionPlugin } from '@udecode/plate-selection/react';
import { Command as CommandPrimitive } from 'cmdk';
import { CircleArrowUp, CircleStop, Loader2Icon } from 'lucide-react';
import {
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import type { AIChatPluginType } from '../editor/plugins/ai-plugin/types.js';
import { useChat } from '../editor/use-chat.js';
import { AIChatEditor } from './ai-chat-editor';
import { AIMenuItems } from './ai-menu-items';
import { Command, CommandList } from './command';
import { Popover, PopoverAnchor, PopoverContent } from './popover';

export function AIMenu({ plugin }: { plugin: AIChatPluginType }) {
  const t = useI18n();

  const { api, editor } = useEditorPlugin(plugin);
  const open = usePluginOption(plugin, 'open');
  const mode = usePluginOption(plugin, 'mode');
  const streaming = usePluginOption(plugin, 'streaming');

  const [value, setValue] = useState('');

  const chat = useChat();

  const { input, messages, setInput, status } = chat;
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [triggerAutoSize, setTriggerAutoSize] = useState('');

  useAutosizeTextarea({
    textAreaRef: textareaRef.current,
    triggerAutoSize: triggerAutoSize,
  });

  const content = useLastAssistantMessage()?.content;

  useEffect(() => {
    if (streaming) {
      const anchor = api.aiChat.node({ anchor: true });
      setTimeout(() => {
        if (!anchor) return;
        const anchorDom = editor.api.toDOMNode(anchor[0]);
        if (anchorDom) {
          setAnchorElement(anchorDom);
        }
      }, 0);
    }
  }, [api.aiChat, editor.api, streaming]);

  useEffect(() => {
    setTriggerAutoSize(input);
  }, [input]);

  const setOpen = useCallback(
    (open: boolean) => {
      if (open) {
        api.aiChat.show();
      } else {
        api.aiChat.hide();
      }
    },
    [api.aiChat]
  );

  const show = useCallback(
    (anchorElement: HTMLElement) => {
      setAnchorElement(anchorElement);
      setOpen(true);
    },
    [setAnchorElement, setOpen]
  );

  const onSubmitChat = useCallback(() => {
    api.aiChat.submit({
      mode: 'chat',
      options: { body: {} },
    });
  }, [api.aiChat]);

  const onStopChat = useCallback(() => {
    api.aiChat.stop();
  }, [api.aiChat]);

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Backspace' && input.length === 0) {
        e.preventDefault();
        api.aiChat.hide();
      }
      if (e.key === 'Enter' && !e.shiftKey && !value) {
        e.preventDefault();
        onSubmitChat();
      }

      e.stopPropagation();
    },
    [api.aiChat, input.length, onSubmitChat, value]
  );

  const onChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const textMessage = e.target.value;

      setTriggerAutoSize(textMessage);
      setInput(textMessage);
    },
    [setInput]
  );

  useEditorChat({
    chat,
    onOpenBlockSelection: (blocks: NodeEntry[]) => {
      const node = blocks.at(-1);
      if (!node) return;
      const blockElement = editor.api.toDOMNode(node[0]);

      if (blockElement) {
        show(blockElement);
      }
    },
    onOpenChange: open => {
      if (!open) {
        setAnchorElement(null);
        setInput('');
      }
    },
    onOpenCursor: () => {
      const blockElement = editor.api.block({ highest: true });

      if (!blockElement) return;

      const [ancestor] = blockElement;

      if (!editor.api.isAt({ end: true }) && !editor.api.isEmpty(ancestor)) {
        editor
          .getApi(BlockSelectionPlugin)
          .blockSelection.set(ancestor.id as string);
      }

      const element = editor.api.toDOMNode(ancestor);

      if (element) {
        show(element);
      }
    },
    onOpenSelection: () => {
      const node = editor.api.blocks().at(-1);
      if (!node) return;
      const element = editor.api.toDOMNode(node[0]);

      if (element) {
        show(element);
      }
    },
  });

  const isLoading = status === 'streaming' || status === 'submitted';

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === 'Escape' && isLoading) {
        e.preventDefault();
        onStopChat();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLoading, onStopChat, open]);

  if (isLoading && mode === 'insert') {
    return null;
  }

  return (
    <Popover modal={false} onOpenChange={setOpen} open={open}>
      <PopoverAnchor
        virtualRef={{
          current: document.body.querySelector('docnboard-chat-panel-ai'),
        }}
      />

      <PopoverContent
        className="border-none bg-transparent p-0 shadow-none"
        onEscapeKeyDown={e => {
          e.preventDefault();
          if (isLoading) return;
          api.aiChat.hide();
        }}
        onFocusOutside={e => {
          e.preventDefault();
        }}
        align="center"
        side="bottom"
        style={{
          width: anchorElement?.offsetWidth,
        }}
      >
        <Command
          className="w-full rounded-lg border shadow-md"
          onValueChange={setValue}
          value={value}
        >
          {mode === 'chat' && content && <AIChatEditor content={content} />}

          {isLoading ? (
            <div className="flex gap-1.5 px-3 text-sm">
              <div className="flex grow items-center gap-2 p-2 text-sm text-muted-foreground select-none">
                <Loader2Icon className="size-4 animate-spin" />
                {messages.length > 1
                  ? t['docnboard.ai.editing']()
                  : t['docnboard.ai.generating']()}
              </div>
              <IconButton onClick={onStopChat}>
                <CircleStop className="stroke-[2px]" />
              </IconButton>
            </div>
          ) : (
            <div className="flex gap-1.5 px-3 text-sm">
              <CommandPrimitive.Input
                asChild
                value={input}
                onValueChange={setInput}
              >
                <div className="flex items-center gap-2 w-full">
                  <SearchIcon className="text-icon" width={20} height={20} />
                  <textarea
                    id="ai-chat-input"
                    ref={textareaRef}
                    autoFocus
                    autoComplete="off"
                    className={cn(
                      'resize-none text-sm disabled:cursor-not-allowed disabled:opacity-50 placeholder:text-muted-foreground/80 max-h-[240px] border-transparent bg-transparent py-2 grow overflow-hidden'
                    )}
                    data-plate-focus
                    onKeyDown={onKeyDown}
                    onChange={onChange}
                    placeholder={t['docnboard.ai.input.placeholder']()}
                    rows={1}
                    value={input}
                  />
                </div>
              </CommandPrimitive.Input>
              <IconButton onClick={onSubmitChat} size="24" disabled={!input}>
                <CircleArrowUp className="stroke-[2px]" />
              </IconButton>
            </div>
          )}

          {!isLoading && (
            <CommandList>
              <AIMenuItems
                setValue={setValue}
                plugin={plugin}
                filterValue={input}
              />
            </CommandList>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
